import { useEffect } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import Icon from '@klv/atoms/icon';
import { LanguageSelector } from '@klv/molecules/language-selector';
import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';
import { generateClassName } from '@klv/shared/utils/functions';

import { Menu } from './children/menu';

import * as styles from './styles/universalFooter.module.scss';

import type { IUniversalFooter, TBackgroundColor } from './types';
import type { IUniversalMenuItemWithCustomFields } from '@klv/shared/types';


const SOCIAL_PLATFORMS = [
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/Klaviyo/',
  },
  {
    name: 'Twitter',
    url: 'https://twitter.com/klaviyo/',
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/klaviyo/',
  },
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/klaviyo/',
  },
] as const;

const moveArrayItem = (arr: IUniversalMenuItemWithCustomFields[], fromIndex: number, toIndex: number) => {
  if (toIndex < 0 || toIndex >= arr.length) return arr;
  const newArr = [...arr];
  newArr.splice(toIndex, 0, newArr.splice(fromIndex, 1)[0]);
  return newArr;
};

export const UniversalFooter = ({
  className = '',
  backgroundColor = 'charcoal',
  fullWidth = false,
  langCode,
  menuItems = [],
  copyrightMenuItems = [],
  navigationStyle = 'default',
  siteMetadata,
  sites = [],
  translations = [],
  ...props
}: IUniversalFooter) => {
  const isDefaultNav = navigationStyle !== 'minimal';

  const { t } = useTranslation();
  const { langCode: siteMetadataLangCode = '' } = siteMetadata || {};

  // Set CSS custom property for content background color when minimal footer
  useEffect(() => {
    if (navigationStyle === 'minimal') {
      // Use the backgroundColor prop to set the content background
      const backgroundColorValue = backgroundColor === 'charcoal' ? 'var(--color-core-charcoal)' : 
                                  backgroundColor === 'cotton' ? 'var(--color-core-cotton)' :
                                  backgroundColor === 'white' ? 'var(--color-core-white)' :
                                  'var(--color-core-cotton)'; // fallback
      
      document.documentElement.style.setProperty('--content-background-color', backgroundColorValue);
    } else {
      document.documentElement.style.removeProperty('--content-background-color');
    }

    // Cleanup function to remove the custom property when component unmounts
    return () => {
      document.documentElement.style.removeProperty('--content-background-color');
    };
  }, [navigationStyle, backgroundColor]);

  const groupedMenuItems = [];
  let mobileItems: IUniversalMenuItemWithCustomFields[] = menuItems;

  // Loop menu items to create desktop groupings and mobile order
  for (let i = 0; i < menuItems.length; i++) {
    const menuItem = menuItems[i];

    // If mobileOrder is set, move the item to the new position in the mobileItems array
    if (menuItem?.mobileOrder) mobileItems = moveArrayItem(mobileItems, i, menuItem.mobileOrder - 1);

    // We group two items at a time, so if we're on the second half of the menu items, we break the loop
    if (i > 3 && i >= menuItems.length / 2) break;

    const childItems = menuItem.childItems?.length ?? 0;
    // Calculate the index of the items pair
    const increaseIndex: number = groupedMenuItems[groupedMenuItems.length - 1]?.length === 1 ? 3 : 4;

    const groupedList: IUniversalMenuItemWithCustomFields[] = [menuItem];
    if (childItems < 9 && menuItems[i + increaseIndex]) groupedList.push(menuItems[i + increaseIndex]);

    groupedMenuItems.push(groupedList);
  }

  return (
    <footer className={generateClassName([className, styles.footer, styles[backgroundColor] as TBackgroundColor, navigationStyle === 'minimal' && styles.minimal, fullWidth && styles.fullWidth])} data-navigation-style={navigationStyle} {...props}>
      <div className={generateClassName(['grid', styles.grid])}>
        <div className={styles.beFooter}><div className='be-ix-link-block'></div></div>
        {isDefaultNav && (
          <nav className={styles.footerNav}>
            <ul className={generateClassName([styles.menuContainer, styles.desktopList])}>
              {groupedMenuItems?.map((groupedMenuItem) => {
                return (
                  <li key={JSON.stringify(groupedMenuItem)} className={styles.menuColumnListItems}>
                    {groupedMenuItem?.map((menuItem) => {
                      return (
                        <div key={menuItem.id} className={styles.menuColumnListItem}>
                          <Menu backgroundColor={backgroundColor} menuItem={menuItem} open={true}/>
                        </div>
                      );
                    })}
                  </li>
                );
              })}
            </ul>
            <ul className={generateClassName([styles.menuContainer, styles.mobileList])}>
              {mobileItems?.map((menuItem) => {
                return (
                  <li key={menuItem.id} className={styles.menuColumnListItem}>
                    <Menu backgroundColor={backgroundColor} menuItem={menuItem}/>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
        <div className={styles.languageSelector}>
          <LanguageSelector
            backgroundColor={backgroundColor}
            currentLanguage={langCode?.toLowerCase() ?? siteMetadataLangCode.toLowerCase()}
            sites={sites}
            translations={translations}
          />
        </div>
        {isDefaultNav && (
          <>
            <div className={styles.logo}>
              <Icon color='currentColor' iconName={"LogoWithRegistered"} style={{ height: 'auto' }} />
              <div className={styles.tagline}>{t('organisms_footer_tagline')}</div>
            </div>
            <div className={styles.social}>
              {SOCIAL_PLATFORMS.map(({ name, url }) => (
                <UniversalMenuItem
                  backgroundColor={backgroundColor}
                  className={styles.socialLink}
                  href={url}
                  key={name}
                  rel='noopener'
                >
                  <Icon color='currentColor' size={1.6} iconName={name} />
                  <span className={styles.socialIconText}>{name}</span>
                </UniversalMenuItem>
              ))}
            </div>
          </>
        )}
        <div className={generateClassName([styles.legal, styles[backgroundColor]])}>
          <div>
            {t('organisms_footer_copyright', { year: (new Date()).getFullYear() })}
          </div>
          <nav className={styles.legalNavigation}>
            {copyrightMenuItems?.map((menuItem) => (
              <UniversalMenuItem backgroundColor={backgroundColor} key={menuItem.label} className={styles.legalLink} href={menuItem.url} label={menuItem.label} />
            ))}
            <UniversalMenuItem
              backgroundColor={backgroundColor}
              as='button'
              className={generateClassName(['ot-sdk-show-settings', styles.legalLink, styles.privacy, styles[backgroundColor]])}
              label={t('organisms_footer_privacySettings')}
              id='ot-sdk-btn'
              onClick={() => window?.OneTrust?.ToggleInfoDisplay?.()}
              suppressHydrationWarning={true}
            />
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default UniversalFooter;
